import { Link } from "react-router-dom";
import { ArrowRight, BadgeCheck, Timer, Truck, ChefHat } from "lucide-react";
import { Reveal } from "../store/Reveal";
import { ProductCard } from "../store/ProductCard";
import { HERO_IMG } from "./HomeHero";

const SUGHI_IMG = "https://static.wixstatic.com/media/d32254_fd80994d36e54569bbf8f807d4bc11d9~mv2.jpg/v1/fill/w_900,h_700,al_c,q_85/d32254_fd80994d36e54569bbf8f807d4bc11d9~mv2.jpg";

const STEPS = [
  { n: "01", t: "Scegli", d: "Sfoglia il nostro menu e scegli i piatti che preferisci." },
  { n: "02", t: "Ordina", d: "Completa il tuo ordine in pochi click, consegna in tutta Italia." },
  { n: "03", t: "Scalda", d: "Segui il video tutorial e in 5 minuti il piatto è pronto." },
  { n: "04", t: "Mangia", d: "Goditi un piatto italiano genuino, gluten free e delizioso." },
];

const WHY = [
  { icon: BadgeCheck, t: "Prodotto certificato", d: "Tutti i nostri piatti sono certificati e controllati per garantire la massima sicurezza." },
  { icon: Timer, t: "Pronto in 5 minuti", d: "Basta scaldare e servire. La tua cena gourmet è pronta in un attimo." },
  { icon: Truck, t: "Consegne in Italia", d: "Spediamo in tutta Italia con packaging refrigerato e consegna rapida." },
  { icon: ChefHat, t: "Qualità artigianale", d: "Preparati da chef italiani con passione e rispetto della tradizione." },
];

export const StepsSection = () => (
  <section className="max-w-7xl mx-auto px-4 sm:px-8 py-20 lg:py-28">
    <Reveal>
      <p className="font-mono-accent text-xs uppercase tracking-[0.3em] text-[#2E8A57] mb-3">Come funziona</p>
      <h2 className="font-serif-display font-bold text-2xl sm:text-3xl lg:text-4xl text-[#1C1917] tracking-tight max-w-xl">
        Dalla nostra cucina alla tua tavola, in quattro gesti.
      </h2>
    </Reveal>
    <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-[#E7E0D8] border border-[#E7E0D8] rounded-2xl overflow-hidden">
      {STEPS.map((s, i) => (
        <Reveal key={s.n} delay={i * 0.1} className="bg-[#FDFBF7] p-8 lg:p-10 group hover:bg-white transition-colors duration-300">
          <p className="font-mono-accent text-sm text-[#7DC242] mb-6">{s.n}</p>
          <h3 className="font-serif-display font-bold text-2xl text-[#1C1917] group-hover:text-[#2E8A57] transition-colors">{s.t}</h3>
          <p className="mt-3 text-sm text-[#44403C] leading-relaxed">{s.d}</p>
        </Reveal>
      ))}
    </div>
  </section>
);

export const CategoriesSection = () => (
  <section className="max-w-7xl mx-auto px-4 sm:px-8 pb-20 lg:pb-28">
    <div className="grid md:grid-cols-2 gap-6">
      <Reveal>
        <Link to="/prodotti?cat=Primi Piatti Pronti" data-testid="category-card-primi" className="card-dish block relative rounded-[2rem] overflow-hidden aspect-[4/3] group">
          <img src={HERO_IMG} alt="Primi piatti pronti" className="dish-img absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
          <div className="absolute bottom-0 p-8">
            <h3 className="font-serif-display font-bold text-3xl text-white">Piatti Pronti</h3>
            <p className="text-white/80 text-sm mt-1.5">21 primi piatti gluten free, pronti in 5 minuti</p>
          </div>
        </Link>
      </Reveal>
      <Reveal delay={0.12}>
        <Link to="/prodotti?cat=Sughi Pronti" data-testid="category-card-sughi" className="card-dish block relative rounded-[2rem] overflow-hidden aspect-[4/3] group">
          <img src={SUGHI_IMG} alt="Sughi pronti" className="dish-img absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
          <div className="absolute bottom-0 p-8">
            <h3 className="font-serif-display font-bold text-3xl text-white">Sughi Pronti</h3>
            <p className="text-white/80 text-sm mt-1.5">Sughi, salse e brodi pronti gelo monoporzionati</p>
          </div>
        </Link>
      </Reveal>
    </div>
  </section>
);

export const FeaturedSection = ({ featured }) => {
  if (featured.length === 0) return null;
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-8 pb-20 lg:pb-28">
      <Reveal className="flex items-end justify-between mb-10">
        <div>
          <p className="font-mono-accent text-xs uppercase tracking-[0.3em] text-[#2E8A57] mb-3">Dal menu</p>
          <h2 className="font-serif-display font-bold text-2xl sm:text-3xl lg:text-4xl text-[#1C1917] tracking-tight">I piatti del momento</h2>
        </div>
        <Link to="/prodotti" data-testid="see-all-products-link" className="link-underline hidden sm:inline-flex items-center gap-2 text-sm font-semibold text-[#2E8A57]">
          Vedi tutto <ArrowRight className="w-4 h-4" />
        </Link>
      </Reveal>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {featured.map((p, i) => (
          <Reveal key={p.slug} delay={(i % 4) * 0.08}>
            <ProductCard product={p} />
          </Reveal>
        ))}
      </div>
    </section>
  );
};

export const WhySection = () => (
  <section className="max-w-7xl mx-auto px-4 sm:px-8 py-20 lg:py-28">
    <Reveal>
      <h2 className="font-serif-display font-bold text-2xl sm:text-3xl lg:text-4xl text-[#1C1917] tracking-tight mb-14">Perché scegliere noi</h2>
    </Reveal>
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
      {WHY.map((w, i) => (
        <Reveal key={w.t} delay={i * 0.1}>
          <div className="w-12 h-12 rounded-full bg-[#2E8A57]/10 flex items-center justify-center mb-5">
            <w.icon className="w-5 h-5 text-[#2E8A57]" />
          </div>
          <h3 className="font-serif-display font-semibold text-xl text-[#1C1917]">{w.t}</h3>
          <p className="mt-2.5 text-sm text-[#44403C] leading-relaxed">{w.d}</p>
        </Reveal>
      ))}
    </div>
    <Reveal delay={0.2}>
      <Link to="/certificazioni" data-testid="home-certificazioni-link" className="link-underline mt-12 inline-flex items-center gap-2 text-sm font-semibold text-[#2E8A57]">
        Scopri le nostre certificazioni <ArrowRight className="w-4 h-4" />
      </Link>
    </Reveal>
  </section>
);
